import { Expand, Pause, Play, Volume2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/button";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { Form } from "@/features/request/feedback-tool/feedbackTool/video/components/Form.tsx";
import { Points } from "@/features/request/feedback-tool/feedbackTool/video/components/Points.tsx";

import { useCanAddMoreFeedbacks } from "../../hooks/useCanAddMoreFeedbacks";
// import '../../styles/video.css';

export function Video({
  currentTime,
  duration,
  media,
}: {
  currentTime: number;
  duration: number;
  media: string | undefined;
}) {
  const {
    feedback: feedBack,
    formFeedBack,
    img,
    selected,
    setFormFeedBack,
    setSelected,
    setSize,
    size,
    videoRef,
  } = useFeedbackTool();
  const divRef = useRef<HTMLDivElement>(null);
  const video = videoRef.current;
  const { hasRole } = useRoleAccess();
  const isDesigner = hasRole(["designer"]);
  const canNotAddFeedback = useCanAddMoreFeedbacks();
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [vCurrentTime, setVCurrentTime] = useState(0);
  const [vDuration, setVDuration] = useState(0);

  useEffect(() => {
    if (!video) return;
    const timestamp = selected?.timestamp;

    if (typeof timestamp === "number" && !Number.isNaN(timestamp)) {
      video.currentTime = timestamp;
      video.pause();
    }
  }, [selected]);

  useEffect(() => {
    if (!videoRef.current || !media) return;

    const video = videoRef.current;

    const handleLoadedMetadata = () => {
      video.currentTime = 0;
      video.click();
      setFormFeedBack(null);
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) return;

    const updateProgress = () => {
      setVCurrentTime(video.currentTime);
      setProgress((video.currentTime / video.duration) * 100);
    };

    const setVideoDuration = () => {
      setVDuration(video.duration);
    };

    video.addEventListener("timeupdate", updateProgress);
    video.addEventListener("loadedmetadata", setVideoDuration);

    return () => {
      video.removeEventListener("timeupdate", updateProgress);
      video.removeEventListener("loadedmetadata", setVideoDuration);
    };
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;

    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;

    if (!video) return;

    const newTime = (Number(e.target.value) / 100) * video.duration;

    video.currentTime = newTime;
    setProgress(Number(e.target.value));
  };

  return (
    <div className="relative cursor-crosshair inline-block" ref={divRef}>
      <Form />
      <Points currentTime={currentTime} />
      <video
        controls={false}
        controlsList="nofullscreen"
        onClick={(event) => {
          if (isDesigner || canNotAddFeedback) return;
          const video = videoRef.current;

          if (!video) return;
          if (selected) return setSelected(null);

          video.pause();
          setIsPlaying(false);

          const clickX = event.clientX;
          const clickY = event.clientY;

          const videoRect = video.getBoundingClientRect();
          const videoWidth = videoRect.width;
          const videoHeight = videoRect.height + 55;

          const relativeX = clickX - videoRect.left;
          const relativeY = clickY - videoRect.top;

          const percentX = (relativeX / videoWidth) * 100;
          const percentY = (relativeY / videoHeight) * 100;

          setFormFeedBack({
            description: "",
            format: "VIDEO",
            id: feedBack ? feedBack.length + 1 : 1,
            imgId: img.id,
            storyId: 0,
            timestamp: video.currentTime,
            type: "ADD",
            user: {
              id: 0,
              name: "",
            },
            x: percentX,
            y: percentY,
          });
        }}
        ref={videoRef}
        style={{
          display: "block",
          maxHeight: 600,
          maxWidth: "100%",
          objectFit: "cover",
          position: "relative",
        }}
      >
        <source src={media} type="video/mp4" />
        You browser don&apos;t support the video tag.
      </video>
      <div className="flex justify-center flex-col gap-1 mt-3 mb-3">
        <input
          className="w-full h-2 rounded-lg accent-fg-brand-primary cursor-pointer"
          max={100}
          min={0}
          onChange={handleSeek}
          step={0.1}
          type="range"
          value={progress}
        />
        <div className="flex mt-3 items-center justify-center">
          <button className="cursor-pointer" onClick={togglePlay} type="button">
            {isPlaying ? (
              <Pause className="text-slate-500" size={22} />
            ) : (
              <Play className="text-slate-500" size={22} />
            )}
          </button>
          <span className="ml-5 text-slate-500">
            {formatTime(vCurrentTime)} / {formatTime(vDuration)}
          </span>
          <Volume2 className="text-slate-500 ml-auto mr-2" size={18} />
          <input
            className="mr-4 w-24 h-1.5 rounded-lg accent-slate-500 cursor-pointer"
            max={1}
            min={0}
            onChange={(e) => {
              if (videoRef.current) {
                videoRef.current.volume = Number.parseFloat(e.target.value);
              }
            }}
            step={0.01}
            type="range"
          />
          <Expand
            className=" text-slate-500 cursor-pointer"
            onClick={() => {
              if (size === "FULL") return setSize("MODAL");

              setSize("FULL");
            }}
            size={20}
          />
        </div>
      </div>
      <div className="absolute bottom-[45px] left-0 w-full h-2">
        {feedBack?.map((item) => {
          const percentage = item?.timestamp
            ? (item?.timestamp / duration) * 100
            : 0;

          return (
            <Button
              className={`
               absolute bg-white w-5 h-7 flex justify-center items-center rounded-full text-black cursor-pointer -top-[16px] border-slate-500
               ${item.id === selected?.id ? "bg-button-primary-bg text-white" : "bg-white text-black"} z-[2] 
              `}
              key={item.id}
              onPress={() => {
                if (formFeedBack) setFormFeedBack(null);
                if (item === selected) {
                  setSelected(null);
                } else {
                  setSelected(item);
                }
              }}
              style={{
                left: `calc(${percentage}% - 17px)`,
              }}
            >
              {item.id}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}
